import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus, ArrowRight, ArrowLeft, X, Upload, Image, Globe, Target } from '@phosphor-icons/react';
import { useAccount, useContractWrite, usePublicClient, useNetwork, useBalance, useWalletClient, useContractRead } from 'wagmi';
import { ADDRESSES, CONFIG, TOKEN_NAMES, TOKEN_SYMBOLS, TOKEN_ICON_SYMBOLS } from '../config';
import { Abi, parseEther, Address, isAddress, formatEther } from 'viem';
import { decodeEventLog } from 'viem';
import { toast } from 'react-toastify';
import { formatTokenAmount } from '../utils/formatting';
import { uploadFileToBackend, uploadDirectoryToBackend, checkBackendHealth } from '../utils/backendUpload';

interface BetWizardProps {
  entityAddress: Address;
  onClose: () => void;
  onSuccess?: (productAddress: `0x${string}`) => void;
}

interface DeploymentDetails {
  hash: string;
  betAddress?: `0x${string}`;
  status: 'pending' | 'complete' | 'error';
  error?: string;
}

interface UIGenerationDetails {
  status: 'pending' | 'complete' | 'error';
  error?: string;
  imageUrl?: string;
  uiPageUrl?: string;
}

export function BetWizard({ entityAddress, onClose, onSuccess }: BetWizardProps) {
  // Step management
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedAssets, setSelectedAssets] = useState<Set<Address>>(new Set());
  const [assetAmounts, setAssetAmounts] = useState<Record<string, string>>({});
  const [approvalStatus, setApprovalStatus] = useState<Record<string, boolean>>({});
  
  // Bet specific fields
  const [betDescription, setBetDescription] = useState('');
  const [sideA, setSideA] = useState('');
  const [sideB, setSideB] = useState('');
  const [oddsA, setOddsA] = useState('');
  const [oddsB, setOddsB] = useState('');
  const [oracleAddress, setOracleAddress] = useState('');
  const [expiryTime, setExpiryTime] = useState('');
  
  // UI Generation
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isGeneratingUI, setIsGeneratingUI] = useState(false);
  const [uiGenerationDetails, setUiGenerationDetails] = useState<UIGenerationDetails | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Transaction states
  const [isDeploying, setIsDeploying] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isFunding, setIsFunding] = useState(false);
  const [deploymentDetails, setDeploymentDetails] = useState<DeploymentDetails | null>(null);
  
  // Wagmi hooks
  const { address } = useAccount();
  const { chain } = useNetwork();
  const publicClient = usePublicClient();
  const { data: walletClient } = useWalletClient();

  // Available assets
  const availableAssets = [
    { address: ADDRESSES.FED as Address, name: 'FED' },
    { address: ADDRESSES.TRINITY_TOKEN as Address, name: 'Trinity Token' },
    { address: ADDRESSES.TBILL as Address, name: 'TBILL' },
    { address: ADDRESSES.PDAI as Address, name: 'pDAI' },
  ];

  // Get balances for all assets
  const assetBalances = availableAssets.map(asset => {
    const { data: balance } = useBalance({
      address,
      token: asset.address,
      watch: true,
    });
    return { ...asset, balance };
  });

  // Step 1: Deploy Bet Contract
  const deployProductArgs = useMemo(() => [
    'TrinityBet',
    ADDRESSES.COUPON_TOKEN as Address,
    '0x0000000000000000000000000000000000000000' as Address,
    name,
    description
  ], [address, name, description]);

  const { write: deployProduct } = useContractWrite({
    address: entityAddress,
    abi: CONFIG.TRINITY_ENTITY_ABI.abi as Abi,
    functionName: 'deployProduct',
    args: deployProductArgs,
    onSuccess: async (tx) => {
      setDeploymentDetails({
        hash: tx.hash,
        status: 'pending'
      });
      
      try {
        const receipt = await publicClient.waitForTransactionReceipt({ hash: tx.hash });
        const productDeployedEvent = receipt.logs.find(log => {
          try {
            if (log.address.toLowerCase() !== entityAddress.toLowerCase()) return false;
            const event = decodeEventLog({
              abi: CONFIG.TRINITY_ENTITY_ABI.abi as Abi,
              data: log.data,
              topics: log.topics
            });
            return event.eventName === 'ProductDeployed';
          } catch {
            return false;
          }
        });

        if (!productDeployedEvent) throw new Error('No ProductDeployed event found');

        const event = decodeEventLog({
          abi: CONFIG.TRINITY_ENTITY_ABI.abi as Abi,
          data: productDeployedEvent.data,
          topics: productDeployedEvent.topics
        }) as unknown as { eventName: 'ProductDeployed'; args: { product: Address } };

        setDeploymentDetails({
          hash: tx.hash,
          betAddress: event.args.product,
          status: 'complete'
        });
        
        // Move to next step
        setStep(3);
        setIsDeploying(false);
        toast.success('Bet contract created successfully!');
      } catch (error) {
        setDeploymentDetails(prev => ({
          ...prev!,
          status: 'error',
          error: error instanceof Error ? error.message : 'Unknown error'
        }));
        setIsDeploying(false);
        toast.error('Failed to create bet contract');
      }
    }
  });

  const handleSubmit = async () => {
    if (!name.trim() || !description.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!betDescription.trim()) {
      toast.error('Please enter a bet description');
      return;
    }

    if (!sideA.trim() || !sideB.trim()) {
      toast.error('Please enter both sides of the bet');
      return;
    }

    if (!oddsA.trim() || !oddsB.trim()) {
      toast.error('Please enter odds for both sides');
      return;
    }

    if (!isAddress(oracleAddress)) {
      toast.error('Please enter a valid oracle address');
      return;
    }

    if (!expiryTime.trim()) {
      toast.error('Please enter expiry time');
      return;
    }

    setIsDeploying(true);
    deployProduct?.();
  };

  const handleGenerateUI = async () => {
    if (!deploymentDetails?.betAddress) {
      toast.error('No bet contract address available');
      return;
    }

    setIsGeneratingUI(true);
    setUiGenerationDetails({ status: 'pending' });

    try {
      // Check backend health
      const isHealthy = await checkBackendHealth();
      if (!isHealthy) {
        throw new Error('Backend is not available');
      }

      // Generate Bet UI template
      const betData = {
        betName: name,
        betDescription: description,
        betAddress: deploymentDetails.betAddress,
        imageUrl: imagePreview || '',
        supportedAssets: Array.from(selectedAssets),
        betDescription: betDescription,
        sideA,
        sideB,
        oddsA,
        oddsB,
        oracleAddress,
        expiryTime
      };

      const templateFiles = generateBetUITemplate(betData);
      
      // Upload template to backend
      const uploadResult = await uploadDirectoryToBackend(templateFiles, 'bet-template');
      
      if (uploadResult.success && uploadResult.cid) {
        setUiGenerationDetails({
          status: 'complete',
          uiPageUrl: uploadResult.cid
        });
        
        // Set UI page on the contract
        if (deploymentDetails.betAddress) {
          const betContract = {
            address: deploymentDetails.betAddress,
            abi: CONFIG.TRINITY_PRODUCT_ABI.abi as Abi
          };
          
          // Get properties contract
          const propertiesAddress = await publicClient.readContract({
            ...betContract,
            functionName: 'properties'
          });
          
          // Set UI page
          const propertiesContract = {
            address: propertiesAddress as Address,
            abi: CONFIG.TRINITY_PROPERTIES_ABI.abi as Abi
          };
          
          await publicClient.writeContract({
            ...propertiesContract,
            functionName: 'setUIpage',
            args: [1, uploadResult.cid]
          });
          
          toast.success('Bet UI template generated and uploaded successfully!');
          onSuccess?.(deploymentDetails.betAddress);
        }
      } else {
        throw new Error('Failed to upload template');
      }
    } catch (error) {
      setUiGenerationDetails({
        status: 'error',
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      toast.error('Failed to generate UI template');
    } finally {
      setIsGeneratingUI(false);
    }
  };

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageDrop = (event: React.DragEvent) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
  };

  const removeImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDeploy = () => {
    handleSubmit();
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-gray-900 rounded-2xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-white">Create Betting Platform</h2>
              <p className="text-gray-400">Step {step} of 3</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X size={24} />
            </button>
          </div>

          {/* Step 1: Basic Information */}
          {step === 1 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-6"
            >
              <div>
                <h3 className="text-xl font-semibold text-white mb-4">Basic Information</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Platform Name
                    </label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter platform name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Description
                    </label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={3}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter description"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Bet Description
                    </label>
                    <textarea
                      value={betDescription}
                      onChange={(e) => setBetDescription(e.target.value)}
                      rows={3}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., Lakers will beat Celtics in game 3 by 5 points"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Side A
                      </label>
                      <input
                        type="text"
                        value={sideA}
                        onChange={(e) => setSideA(e.target.value)}
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., Lakers"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Side B
                      </label>
                      <input
                        type="text"
                        value={sideB}
                        onChange={(e) => setSideB(e.target.value)}
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., Celtics"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Odds A (e.g., 3 for 3:2)
                      </label>
                      <input
                        type="number"
                        value={oddsA}
                        onChange={(e) => setOddsA(e.target.value)}
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="3"
                        min="1"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Odds B (e.g., 2 for 3:2)
                      </label>
                      <input
                        type="number"
                        value={oddsB}
                        onChange={(e) => setOddsB(e.target.value)}
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="2"
                        min="1"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Oracle Address
                    </label>
                    <input
                      type="text"
                      value={oracleAddress}
                      onChange={(e) => setOracleAddress(e.target.value)}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="0x..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Expiry Time (hours from now)
                    </label>
                    <input
                      type="number"
                      value={expiryTime}
                      onChange={(e) => setExpiryTime(e.target.value)}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="24"
                      min="1"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-between">
                <button
                  onClick={onClose}
                  className="px-6 py-3 text-gray-400 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => setStep(2)}
                  disabled={!name.trim() || !description.trim() || !betDescription.trim() || !sideA.trim() || !sideB.trim() || !oddsA.trim() || !oddsB.trim() || !oracleAddress.trim() || !expiryTime.trim()}
                  className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg text-white font-medium transition-colors"
                >
                  Next
                </button>
              </div>
            </motion.div>
          )}

          {/* Step 2: Asset Configuration */}
          {step === 2 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-6"
            >
              <div>
                <h3 className="text-xl font-semibold text-white mb-4">Asset Configuration</h3>
                
                <div className="space-y-4">
                  <h4 className="text-lg font-medium text-white">Supported Assets</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {assetBalances.map((asset) => (
                      <div
                        key={asset.address}
                        className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          selectedAssets.has(asset.address)
                            ? 'border-blue-500 bg-blue-500/10'
                            : 'border-gray-700 bg-gray-800 hover:border-gray-600'
                        }`}
                        onClick={() => {
                          const newSelected = new Set(selectedAssets);
                          if (newSelected.has(asset.address)) {
                            newSelected.delete(asset.address);
                          } else {
                            newSelected.add(asset.address);
                          }
                          setSelectedAssets(newSelected);
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h5 className="font-medium text-white">{asset.name}</h5>
                            <p className="text-sm text-gray-400">
                              Balance: {asset.balance ? formatTokenAmount(asset.balance) : '0'}
                            </p>
                          </div>
                          <div className="text-2xl">
                            {TOKEN_ICON_SYMBOLS[asset.address as keyof typeof TOKEN_ICON_SYMBOLS] || '?'}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setStep(1)}
                  className="px-6 py-3 text-gray-400 hover:text-white transition-colors"
                >
                  <ArrowLeft size={16} className="inline mr-2" />
                  Back
                </button>
                <button
                  onClick={handleDeploy}
                  disabled={isDeploying || selectedAssets.size === 0}
                  className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg text-white font-medium transition-colors"
                >
                  {isDeploying ? 'Deploying...' : 'Deploy Bet Platform'}
                </button>
              </div>
            </motion.div>
          )}

          {/* Step 3: UI Generation */}
          {step === 3 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-6"
            >
              <div>
                <h3 className="text-xl font-semibold text-white mb-4">Generate UI Template</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Platform Image (Optional)
                    </label>
                    <div
                      className="border-2 border-dashed border-gray-600 rounded-lg p-6 text-center cursor-pointer hover:border-gray-500 transition-colors"
                      onDrop={handleImageDrop}
                      onDragOver={handleDragOver}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {imagePreview ? (
                        <div className="space-y-2">
                          <img src={imagePreview} alt="Preview" className="w-32 h-32 object-cover mx-auto rounded-lg" />
                          <button
                            onClick={removeImage}
                            className="text-red-400 hover:text-red-300"
                          >
                            Remove Image
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <Image size={48} className="mx-auto text-gray-400" />
                          <p className="text-gray-400">Click to upload or drag and drop</p>
                        </div>
                      )}
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageSelect}
                      className="hidden"
                    />
                  </div>

                  {deploymentDetails?.status === 'complete' && (
                    <div className="bg-green-900/20 border border-green-500/20 rounded-lg p-4">
                      <p className="text-green-400">
                        ✅ Bet contract deployed successfully at: {deploymentDetails.betAddress}
                      </p>
                    </div>
                  )}

                  {uiGenerationDetails?.status === 'complete' && (
                    <div className="bg-blue-900/20 border border-blue-500/20 rounded-lg p-4">
                      <p className="text-blue-400">
                        ✅ UI template generated and uploaded successfully!
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setStep(2)}
                  className="px-6 py-3 text-gray-400 hover:text-white transition-colors"
                >
                  <ArrowLeft size={16} className="inline mr-2" />
                  Back
                </button>
                <button
                  onClick={handleGenerateUI}
                  disabled={isGeneratingUI || !deploymentDetails?.betAddress}
                  className="px-6 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg text-white font-medium transition-colors"
                >
                  {isGeneratingUI ? 'Generating...' : 'Generate UI Template'}
                </button>
              </div>
            </motion.div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function generateBetUITemplate(data: {
  betName: string;
  betDescription: string;
  betAddress: string;
  imageUrl: string;
  supportedAssets: Address[];
  betDescription: string;
  sideA: string;
  sideB: string;
  oddsA: string;
  oddsB: string;
  oracleAddress: string;
  expiryTime: string;
}): { [filename: string]: string } {
  const { betName, betDescription, betAddress, imageUrl, supportedAssets, betDescription: betDesc, sideA, sideB, oddsA, oddsB, oracleAddress, expiryTime } = data;

  return {
    'index.html': `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${betName} - Betting Platform</title>
    <link rel="stylesheet" href="style.css">
    <script src="lib/web3.min.js"></script>
</head>
<body>
    <div class="container">
        <header class="header">
            <div class="logo">
                <img src="lib/fed.png" alt="FED" class="logo-img">
                <img src="lib/pdai.png" alt="PDAI" class="logo-img">
            </div>
            <h1 class="title">${betName}</h1>
            <p class="subtitle">${betDescription}</p>
        </header>

        <main class="main">
            <div class="bet-details">
                <h2>Betting Event</h2>
                <div class="bet-info">
                    <p class="bet-description">${betDesc}</p>
                    <div class="sides">
                        <div class="side">
                            <h3>${sideA}</h3>
                            <p class="odds">Odds: ${oddsA}:${oddsB}</p>
                        </div>
                        <div class="vs">VS</div>
                        <div class="side">
                            <h3>${sideB}</h3>
                            <p class="odds">Odds: ${oddsB}:${oddsA}</p>
                        </div>
                    </div>
                </div>
            </div>

            <div class="betting-section">
                <h3>Place Your Bet</h3>
                <div class="bet-form">
                    <div class="form-group">
                        <label>Select Side:</label>
                        <select id="betSide" class="form-control">
                            <option value="1">${sideA}</option>
                            <option value="2">${sideB}</option>
                        </select>
                    </div>
                    <div class="form-group">
                        <label>Bet Amount:</label>
                        <input type="number" id="betAmount" class="form-control" placeholder="Enter amount" min="1">
                    </div>
                    <button id="placeBetBtn" class="btn btn-primary">Place Bet</button>
                </div>
            </div>

            <div class="actions">
                <button id="claimRewardBtn" class="btn btn-success" disabled>Claim Reward</button>
                <button id="refundBetBtn" class="btn btn-warning" disabled>Refund Bet</button>
            </div>

            <div id="status" class="status"></div>
        </main>
    </div>

    <script src="script.js"></script>
</body>
</html>`,

    'style.css': `
* {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
}

body {
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    min-height: 100vh;
    color: #333;
}

.container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 20px;
}

.header {
    text-align: center;
    margin-bottom: 40px;
    background: rgba(255, 255, 255, 0.95);
    padding: 30px;
    border-radius: 15px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.logo {
    display: flex;
    justify-content: center;
    gap: 20px;
    margin-bottom: 20px;
}

.logo-img {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.title {
    font-size: 2.5rem;
    font-weight: 700;
    color: #2c3e50;
    margin-bottom: 10px;
}

.subtitle {
    font-size: 1.1rem;
    color: #7f8c8d;
    font-weight: 300;
}

.main {
    background: rgba(255, 255, 255, 0.95);
    border-radius: 15px;
    padding: 30px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.bet-details {
    margin-bottom: 30px;
}

.bet-details h2 {
    font-size: 1.8rem;
    color: #2c3e50;
    margin-bottom: 20px;
    text-align: center;
}

.bet-info {
    background: #f8f9fa;
    padding: 25px;
    border-radius: 10px;
    border-left: 4px solid #3498db;
}

.bet-description {
    font-size: 1.1rem;
    color: #2c3e50;
    margin-bottom: 20px;
    text-align: center;
    font-weight: 500;
}

.sides {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
}

.side {
    flex: 1;
    text-align: center;
    padding: 20px;
    background: white;
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.side h3 {
    font-size: 1.4rem;
    color: #2c3e50;
    margin-bottom: 10px;
}

.odds {
    font-size: 1.1rem;
    color: #27ae60;
    font-weight: 600;
}

.vs {
    font-size: 2rem;
    font-weight: 700;
    color: #e74c3c;
}

.betting-section {
    margin-bottom: 30px;
}

.betting-section h3 {
    font-size: 1.4rem;
    color: #2c3e50;
    margin-bottom: 20px;
    text-align: center;
}

.bet-form {
    background: #f8f9fa;
    padding: 25px;
    border-radius: 10px;
    border-left: 4px solid #27ae60;
}

.form-group {
    margin-bottom: 20px;
}

.form-group label {
    display: block;
    font-weight: 600;
    color: #2c3e50;
    margin-bottom: 8px;
}

.form-control {
    width: 100%;
    padding: 12px;
    border: 2px solid #ddd;
    border-radius: 8px;
    font-size: 1rem;
    transition: border-color 0.3s ease;
}

.form-control:focus {
    outline: none;
    border-color: #3498db;
}

.actions {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 15px;
    margin-bottom: 20px;
}

.btn {
    padding: 15px 25px;
    border: none;
    border-radius: 8px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.btn-primary {
    background: linear-gradient(135deg, #3498db, #2980b9);
    color: white;
}

.btn-primary:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(52, 152, 219, 0.4);
}

.btn-success {
    background: linear-gradient(135deg, #27ae60, #229954);
    color: white;
}

.btn-success:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(39, 174, 96, 0.4);
}

.btn-warning {
    background: linear-gradient(135deg, #f39c12, #e67e22);
    color: white;
}

.btn-warning:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(243, 156, 18, 0.4);
}

.status {
    padding: 20px;
    border-radius: 10px;
    text-align: center;
    font-weight: 600;
    margin-top: 20px;
}

.status.success {
    background: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
}

.status.error {
    background: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
}

.status.info {
    background: #d1ecf1;
    color: #0c5460;
    border: 1px solid #bee5eb;
}

@media (max-width: 768px) {
    .container {
        padding: 10px;
    }
    
    .title {
        font-size: 2rem;
    }
    
    .sides {
        flex-direction: column;
    }
    
    .vs {
        font-size: 1.5rem;
    }
    
    .actions {
        grid-template-columns: 1fr;
    }
}`,

    'script.js': `
// Betting Platform Management Script
class BettingPlatform {
    constructor() {
        this.web3 = null;
        this.contract = null;
        this.account = null;
        this.betAddress = '${betAddress}';
        this.sideA = '${sideA}';
        this.sideB = '${sideB}';
        this.oddsA = ${oddsA};
        this.oddsB = ${oddsB};
        this.oracleAddress = '${oracleAddress}';
        this.expiryTime = ${expiryTime};
        
        this.init();
    }

    async init() {
        try {
            // Check if MetaMask is installed
            if (typeof window.ethereum !== 'undefined') {
                this.web3 = new Web3(window.ethereum);
                
                // Request account access
                const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
                this.account = accounts[0];
                
                console.log('Connected to MetaMask:', this.account);
                this.updateStatus('Connected to MetaMask', 'info');
                
                // Initialize contract
                await this.initContract();
                
                // Set up event listeners
                this.setupEventListeners();
                
            } else {
                this.updateStatus('Please install MetaMask to use this application', 'error');
            }
        } catch (error) {
            console.error('Initialization error:', error);
            this.updateStatus('Failed to connect to MetaMask', 'error');
        }
    }

    async initContract() {
        try {
            // Basic contract ABI for bet functions
            const contractABI = [
                {
                    "inputs": [
                        {"internalType": "string", "name": "_description", "type": "string"},
                        {"internalType": "address", "name": "_sideA", "type": "address"},
                        {"internalType": "address", "name": "_sideB", "type": "address"},
                        {"internalType": "uint256", "name": "_oddsA", "type": "uint256"},
                        {"internalType": "uint256", "name": "_oddsB", "type": "uint256"},
                        {"internalType": "address", "name": "_oracle", "type": "address"},
                        {"internalType": "uint256", "name": "_expiryTime", "type": "uint256"}
                    ],
                    "name": "createBet",
                    "outputs": [{"internalType": "uint32", "name": "", "type": "uint32"}],
                    "stateMutability": "nonpayable",
                    "type": "function"
                },
                {
                    "inputs": [
                        {"internalType": "uint32", "name": "betId", "type": "uint32"},
                        {"internalType": "uint8", "name": "side", "type": "uint8"},
                        {"internalType": "uint256", "name": "amount", "type": "uint256"}
                    ],
                    "name": "placeBet",
                    "outputs": [],
                    "stateMutability": "nonpayable",
                    "type": "function"
                },
                {
                    "inputs": [{"internalType": "uint32", "name": "betId", "type": "uint32"}],
                    "name": "claimReward",
                    "outputs": [],
                    "stateMutability": "nonpayable",
                    "type": "function"
                },
                {
                    "inputs": [{"internalType": "uint32", "name": "betId", "type": "uint32"}],
                    "name": "refundBet",
                    "outputs": [],
                    "stateMutability": "nonpayable",
                    "type": "function"
                },
                {
                    "inputs": [{"internalType": "uint32", "name": "betId", "type": "uint32"}],
                    "name": "getBet",
                    "outputs": [
                        {"internalType": "string", "name": "description", "type": "string"},
                        {"internalType": "address", "name": "sideA", "type": "address"},
                        {"internalType": "address", "name": "sideB", "type": "address"},
                        {"internalType": "uint256", "name": "oddsA", "type": "uint256"},
                        {"internalType": "uint256", "name": "oddsB", "type": "uint256"},
                        {"internalType": "address", "name": "oracle", "type": "address"},
                        {"internalType": "uint256", "name": "totalPoolA", "type": "uint256"},
                        {"internalType": "uint256", "name": "totalPoolB", "type": "uint256"},
                        {"internalType": "uint256", "name": "expiryTime", "type": "uint256"},
                        {"internalType": "bool", "name": "finalized", "type": "bool"},
                        {"internalType": "uint8", "name": "winner", "type": "uint8"},
                        {"internalType": "uint8", "name": "status", "type": "uint8"}
                    ],
                    "stateMutability": "view",
                    "type": "function"
                },
                {
                    "inputs": [
                        {"internalType": "uint32", "name": "betId", "type": "uint32"},
                        {"internalType": "address", "name": "user", "type": "address"}
                    ],
                    "name": "getUserBet",
                    "outputs": [
                        {"internalType": "address", "name": "user", "type": "address"},
                        {"internalType": "uint8", "name": "side", "type": "uint8"},
                        {"internalType": "uint256", "name": "amount", "type": "uint256"},
                        {"internalType": "bool", "name": "claimed", "type": "bool"}
                    ],
                    "stateMutability": "view",
                    "type": "function"
                }
            ];

            this.contract = new this.web3.eth.Contract(contractABI, this.betAddress);
            console.log('Contract initialized:', this.betAddress);
            
        } catch (error) {
            console.error('Contract initialization error:', error);
            this.updateStatus('Failed to initialize contract', 'error');
        }
    }

    setupEventListeners() {
        const placeBetBtn = document.getElementById('placeBetBtn');
        const claimRewardBtn = document.getElementById('claimRewardBtn');
        const refundBetBtn = document.getElementById('refundBetBtn');

        if (placeBetBtn) {
            placeBetBtn.addEventListener('click', () => this.placeBet());
        }
        if (claimRewardBtn) {
            claimRewardBtn.addEventListener('click', () => this.claimReward());
        }
        if (refundBetBtn) {
            refundBetBtn.addEventListener('click', () => this.refundBet());
        }
    }

    async placeBet() {
        try {
            const side = parseInt(document.getElementById('betSide').value);
            const amount = document.getElementById('betAmount').value;
            
            if (!amount || amount <= 0) {
                this.updateStatus('Please enter a valid bet amount', 'error');
                return;
            }

            this.updateStatus('Placing bet...', 'info');
            
            const result = await this.contract.methods.placeBet(0, side, this.web3.utils.toWei(amount, 'ether')).send({
                from: this.account,
                gas: 300000
            });
            
            console.log('Bet placed:', result);
            this.updateStatus('Bet placed successfully!', 'success');
            
        } catch (error) {
            console.error('Place bet error:', error);
            this.updateStatus('Failed to place bet: ' + error.message, 'error');
        }
    }

    async claimReward() {
        try {
            this.updateStatus('Claiming reward...', 'info');
            
            const result = await this.contract.methods.claimReward(0).send({
                from: this.account,
                gas: 300000
            });
            
            console.log('Reward claimed:', result);
            this.updateStatus('Reward claimed successfully!', 'success');
            
        } catch (error) {
            console.error('Claim reward error:', error);
            this.updateStatus('Failed to claim reward: ' + error.message, 'error');
        }
    }

    async refundBet() {
        try {
            this.updateStatus('Refunding bet...', 'info');
            
            const result = await this.contract.methods.refundBet(0).send({
                from: this.account,
                gas: 300000
            });
            
            console.log('Bet refunded:', result);
            this.updateStatus('Bet refunded successfully!', 'success');
            
        } catch (error) {
            console.error('Refund bet error:', error);
            this.updateStatus('Failed to refund bet: ' + error.message, 'error');
        }
    }

    updateStatus(message, type) {
        const statusElement = document.getElementById('status');
        if (statusElement) {
            statusElement.textContent = message;
            statusElement.className = 'status ' + type;
        }
        console.log('Status:', message);
    }
}

// Initialize the Betting Platform when the page loads
document.addEventListener('DOMContentLoaded', () => {
    new BettingPlatform();
});`,

    'lib/web3.min.js': `// Web3.js library content would be here`,
    'lib/fed.png': `// FED token image`,
    'lib/pdai.png': `// PDAI token image`
  };
} 